<script setup>
/**
 * Reusable library + upload file picker dialog.
 *
 * Expected item shape:
 *   { id, name, updated_at?, created_at?, typeLabel?,
 *     file?: { filename?, extension?, url?, web_url?, thumbnail? } }
 *
 * Expected source shape:
 *   { id, label, icon? }
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  sources: { type: Array, default: () => [] },
  items: { type: Array, default: () => [] },
  selectedSourceId: { type: [String, Number], default: null },
  sourceLoading: { type: Boolean, default: false },
  excludedIds: { type: Array, default: () => [] },
  uploading: { type: Boolean, default: false },
  multiple: { type: Boolean, default: true },
  accept: { type: String, default: '' },
  showPreview: { type: Boolean, default: true },
  showUploadTab: { type: Boolean, default: true },
  uploadHint: { type: String, default: '' },
  searchPlaceholder: { type: String, default: '' },
  emptyLabel: { type: String, default: '' },
  noResultsLabel: { type: String, default: '' },
  confirmLabel: { type: String, default: '' },
  confirmLabelWithCount: { type: String, default: '' },
});

const emit = defineEmits([
  'update:modelValue',
  'update:selectedSourceId',
  'select',
  'upload',
  'refresh-source',
  'close',
]);

const { t } = useI18n();
const dialogRef = ref(null);
const fileInput = ref(null);
const listRef = ref(null);
const sourceTabsRef = ref(null);
const searchQuery = ref('');
const selectedIds = ref(new Set());
const highlightIndex = ref(-1);
const previewItem = ref(null);
const previewBroken = ref(false);
const hoverArmed = ref(false);
const PREVIEW_DELAY_MS = 450;
let previewTimer = null;

const armHoverOnPointerMove = () => {
  hoverArmed.value = true;
  window.removeEventListener('pointermove', armHoverOnPointerMove);
};

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
]);
const IFRAME_EXTENSIONS = new Set(['pdf', 'txt', 'md']);

const sourceTabs = computed(() =>
  (props.sources || []).map(source => ({
    key: `source:${source.id}`,
    sourceId: source.id,
    label: source.label,
    icon: source.icon || 'i-lucide-folder',
  }))
);

const activeSourceKey = computed(() => {
  if (props.selectedSourceId != null) return `source:${props.selectedSourceId}`;
  return sourceTabs.value[0]?.key || null;
});

const activeSource = computed(() =>
  (props.sources || []).find(
    source => String(source.id) === String(props.selectedSourceId)
  )
);

const availableItems = computed(() => {
  const excluded = new Set(props.excludedIds);
  return (props.items || []).filter(item => !excluded.has(item.id));
});

const filteredItems = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return availableItems.value;
  return availableItems.value.filter(item => {
    const name = String(item.name || '').toLowerCase();
    const filename = String(item.file?.filename || '').toLowerCase();
    return name.includes(q) || filename.includes(q);
  });
});

const selectedCount = computed(() => selectedIds.value.size);

const canConfirm = computed(
  () => selectedCount.value > 0 && !props.uploading
);

const previewExtension = computed(() =>
  String(
    previewItem.value?.file?.extension ||
      previewItem.value?.name?.split('.').pop() ||
      ''
  ).toLowerCase()
);

const isImagePreview = computed(() =>
  IMAGE_EXTENSIONS.has(previewExtension.value)
);

const isIframePreview = computed(() =>
  IFRAME_EXTENSIONS.has(previewExtension.value)
);

const previewUrl = computed(() => {
  const file = previewItem.value?.file;
  if (!file) return null;
  if (isImagePreview.value) return file.web_url || file.thumbnail || file.url;
  const url = file.url;
  if (!url) return null;
  // Hide Chrome/Edge built-in PDF viewer chrome (toolbar, side panes)
  if (previewExtension.value === 'pdf' && !url.includes('#')) {
    return `${url}#toolbar=0&navpanes=0&scrollbar=0`;
  }
  return url;
});

const previewMetaLabel = computed(() => {
  if (previewItem.value?.typeLabel) return previewItem.value.typeLabel;
  if (activeSource.value?.label) return activeSource.value.label;
  return t('FILE_PICKER.TYPE_FILE');
});

const canShowPreview = computed(
  () =>
    props.showPreview &&
    !!previewItem.value &&
    !!previewUrl.value &&
    !previewBroken.value &&
    (isImagePreview.value || isIframePreview.value)
);

const clearPreviewTimer = () => {
  if (previewTimer) {
    clearTimeout(previewTimer);
    previewTimer = null;
  }
};

const clearPreview = () => {
  clearPreviewTimer();
  previewItem.value = null;
  previewBroken.value = false;
};

const schedulePreview = index => {
  clearPreviewTimer();
  if (index < 0) {
    previewItem.value = null;
    previewBroken.value = false;
    return;
  }
  previewTimer = setTimeout(() => {
    previewBroken.value = false;
    previewItem.value = filteredItems.value[index] || null;
    previewTimer = null;
  }, PREVIEW_DELAY_MS);
};

const resolvedUploadHint = computed(
  () => props.uploadHint || t('FILE_PICKER.UPLOAD_HINT')
);

const resolvedSearchPlaceholder = computed(
  () => props.searchPlaceholder || t('FILE_PICKER.SEARCH')
);

const resolvedEmptyLabel = computed(
  () => props.emptyLabel || t('FILE_PICKER.EMPTY')
);

const resolvedNoResultsLabel = computed(
  () => props.noResultsLabel || t('FILE_PICKER.NO_RESULTS')
);

const resolvedConfirmLabel = computed(() => {
  if (selectedCount.value && props.confirmLabelWithCount) {
    return props.confirmLabelWithCount.replace(
      '{count}',
      String(selectedCount.value)
    );
  }
  if (selectedCount.value) {
    return t('FILE_PICKER.ADD_COUNT', { count: selectedCount.value });
  }
  return props.confirmLabel || t('FILE_PICKER.ADD');
});

const formatItemDate = item => {
  const raw = item.updated_at || item.created_at;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const itemExtension = item => {
  const ext = item.file?.extension || item.name?.split('.').pop();
  return String(ext || 'file').toUpperCase().slice(0, 4);
};

const isSelected = id => selectedIds.value.has(id);

const toggleSelect = id => {
  if (!props.multiple) {
    selectedIds.value = new Set([id]);
    return;
  }
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};

const scrollActiveSourceIntoView = async () => {
  await nextTick();
  const active = sourceTabsRef.value?.querySelector('[data-active-source="true"]');
  active?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
};

const selectSource = tab => {
  selectedIds.value = new Set();
  highlightIndex.value = -1;
  clearPreview();
  searchQuery.value = '';
  emit('update:selectedSourceId', tab.sourceId);
  emit('refresh-source', tab.sourceId);
  scrollActiveSourceIntoView();
};

const resetState = () => {
  searchQuery.value = '';
  selectedIds.value = new Set();
  highlightIndex.value = -1;
  hoverArmed.value = false;
  clearPreview();
};

const onRowMouseEnter = index => {
  // Ignore hover until the pointer actually moves — opening under the cursor
  // would otherwise immediately highlight the first row and show a preview.
  if (!hoverArmed.value) return;
  highlightIndex.value = index;
};

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};

const scrollHighlightIntoView = async () => {
  await nextTick();
  const row = listRef.value?.querySelector('[data-highlighted="true"]');
  row?.scrollIntoView({ block: 'nearest' });
};

const triggerUpload = () => {
  nextTick(() => fileInput.value?.click());
};

const onFileChange = event => {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  if (!files.length) return;
  emit('upload', { files, file: files[0] });
  handleClose();
};

const confirmSelection = () => {
  if (!canConfirm.value) return;
  emit('select', { ids: [...selectedIds.value] });
  handleClose();
};

const onKeydown = event => {
  if (!props.modelValue) return;
  const tag = event.target?.tagName;
  const isTyping =
    tag === 'TEXTAREA' ||
    (tag === 'INPUT' &&
      event.target?.type !== 'search' &&
      event.target?.type !== 'checkbox');
  if (isTyping) return;

  const items = filteredItems.value;
  const tabs = sourceTabs.value;
  const currentIndex = tabs.findIndex(tab => tab.key === activeSourceKey.value);

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (!items.length) return;
    highlightIndex.value =
      highlightIndex.value < 0
        ? 0
        : (highlightIndex.value + 1) % items.length;
    return;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (!items.length) return;
    highlightIndex.value =
      highlightIndex.value < 0
        ? items.length - 1
        : (highlightIndex.value - 1 + items.length) % items.length;
    return;
  }
  if (event.key === 'ArrowLeft' && currentIndex > 0) {
    event.preventDefault();
    selectSource(tabs[currentIndex - 1]);
    return;
  }
  if (event.key === 'ArrowRight' && currentIndex < tabs.length - 1) {
    event.preventDefault();
    selectSource(tabs[currentIndex + 1]);
    return;
  }
  if (event.key === 'Enter' && !event.metaKey && !event.ctrlKey) {
    event.preventDefault();
    if (!items.length) {
      if (props.showUploadTab) triggerUpload();
      return;
    }
    const item = items[highlightIndex.value];
    if (item) toggleSelect(item.id);
  }
};

watch(
  () => props.modelValue,
  async val => {
    if (val) {
      resetState();
      emit('refresh-source', props.selectedSourceId);
      await nextTick();
      dialogRef.value?.open();
      window.addEventListener('keydown', onKeydown);
      window.addEventListener('pointermove', armHoverOnPointerMove);
    } else {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('pointermove', armHoverOnPointerMove);
      dialogRef.value?.close();
      clearPreview();
    }
  },
  { immediate: true }
);

watch(filteredItems, items => {
  if (highlightIndex.value >= items.length) {
    highlightIndex.value = -1;
  }
});

watch(highlightIndex, index => {
  schedulePreview(index);
  scrollHighlightIntoView();
});

onBeforeUnmount(() => {
  clearPreviewTimer();
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('pointermove', armHoverOnPointerMove);
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    :is-loading="uploading"
    @close="handleClose"
  >
    <template #header>
      <div class="flex min-w-0 items-center gap-2">
        <div
          class="flex min-w-0 flex-1 items-center gap-2 rounded-lg bg-n-slate-3 px-3 py-2.5 dark:bg-slate-800"
        >
          <Icon icon="i-lucide-search" class="size-4 shrink-0 text-n-slate-10" />
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
            :placeholder="resolvedSearchPlaceholder"
            autofocus
          />
        </div>
        <Button
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          type="button"
          :aria-label="t('DIALOG.BUTTONS.CANCEL')"
          @click="handleClose"
        />
      </div>
    </template>

    <!-- Preview lives inside <dialog> (top layer) but is positioned beside the panel -->
    <div class="relative flex flex-col gap-4 px-1 pb-1">
      <aside
        v-if="canShowPreview"
        class="absolute top-0 z-10 hidden h-[min(28rem,70vh)] w-72 flex-col overflow-hidden rounded-xl border border-n-slate-6 bg-white shadow-xl dark:bg-slate-950 xl:flex ltr:left-[calc(100%+2.5rem)] rtl:right-[calc(100%+2.5rem)]"
      >
        <div
          class="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-n-slate-2 dark:bg-slate-900"
        >
          <img
            v-if="isImagePreview"
            :src="previewUrl"
            :alt="previewItem.name"
            class="max-h-full max-w-full object-contain"
            @error="previewBroken = true"
          />
          <iframe
            v-else
            :src="previewUrl"
            class="h-full w-full border-0"
            :title="t('FILE_PICKER.PREVIEW_TITLE')"
          />
        </div>
        <div class="shrink-0 border-t border-n-slate-6 px-3 py-3">
          <p class="truncate text-sm font-semibold text-n-slate-12">
            {{ previewItem.name }}
          </p>
          <p class="mt-0.5 truncate text-xs text-n-slate-10">
            {{ t('FILE_PICKER.TYPE_FILE') }}
            ·
            {{ previewMetaLabel }}
          </p>
        </div>
      </aside>
      <div
        v-if="sourceTabs.length"
        ref="sourceTabsRef"
        class="-mx-1 flex items-center gap-1 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]"
      >
        <button
          v-for="tab in sourceTabs"
          :key="tab.key"
          type="button"
          class="inline-flex shrink-0 max-w-[12rem] items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors"
          :class="
            activeSourceKey === tab.key
              ? 'bg-n-slate-3 text-n-slate-12 dark:bg-slate-800'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :data-active-source="activeSourceKey === tab.key ? 'true' : null"
          @click="selectSource(tab)"
        >
          <Icon v-if="tab.icon" :icon="tab.icon" class="size-3.5 shrink-0" />
          <span class="truncate">{{ tab.label }}</span>
        </button>
      </div>

      <div v-if="showUploadTab">
        <p
          class="mb-2 text-[0.65rem] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('FILE_PICKER.ACTIONS') }}
        </p>
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-lg bg-n-slate-3 px-3 py-2.5 text-left text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-slate-4 dark:bg-slate-800 dark:hover:bg-slate-700"
          :disabled="uploading"
          @click="triggerUpload"
        >
          <Icon
            icon="i-lucide-upload"
            class="size-4 shrink-0 text-n-slate-11"
          />
          <span class="min-w-0 flex-1">
            {{ t('FILE_PICKER.UPLOAD_COMPUTER') }}
          </span>
          <kbd
            class="hidden rounded border border-n-slate-6 px-1.5 py-0.5 text-[0.65rem] font-medium text-n-slate-10 sm:inline"
          >
            ↵
          </kbd>
        </button>
      </div>

      <div>
        <p
          class="mb-2 text-[0.65rem] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('FILE_PICKER.RECENTS') }}
        </p>

        <div
          v-if="sourceLoading"
          class="flex min-h-[22rem] items-center justify-center gap-2 rounded-lg border border-n-slate-6 text-sm text-n-slate-11"
        >
          <div
            class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
          />
          {{ t('FILE_PICKER.LOADING') }}
        </div>

        <div
          v-else-if="!filteredItems.length"
          class="flex min-h-[22rem] items-center justify-center rounded-lg border border-dashed border-n-slate-6 px-4 text-center text-sm text-n-slate-11"
        >
          {{
            searchQuery.trim() ? resolvedNoResultsLabel : resolvedEmptyLabel
          }}
        </div>

        <div
          v-else
          ref="listRef"
          class="min-h-[22rem] max-h-[22rem] overflow-y-auto rounded-lg border border-n-slate-6"
        >
          <button
            v-for="(item, index) in filteredItems"
            :key="item.id"
            type="button"
            class="flex w-full items-center gap-3 border-b border-n-slate-6 px-3 py-2.5 text-left last:border-b-0 transition-colors"
            :class="
              index === highlightIndex
                ? 'bg-n-slate-3 dark:bg-slate-800'
                : 'hover:bg-n-alpha-2'
            "
            :data-highlighted="index === highlightIndex ? 'true' : null"
            @click="toggleSelect(item.id)"
            @mouseenter="onRowMouseEnter(index)"
          >
            <span
              class="flex size-5 shrink-0 items-center justify-center rounded border transition-colors"
              :class="
                isSelected(item.id)
                  ? 'border-woot-500 bg-woot-500 text-white'
                  : 'border-n-slate-6 bg-white dark:bg-slate-950'
              "
            >
              <Icon
                v-if="isSelected(item.id)"
                icon="i-lucide-check"
                class="size-3.5"
              />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex min-w-0 items-baseline gap-2">
                <span class="truncate text-sm font-medium text-n-slate-12">
                  {{ item.name }}
                </span>
                <span class="shrink-0 text-xs text-n-slate-10">
                  {{ formatItemDate(item) }}
                </span>
              </div>
            </div>
            <span class="shrink-0 text-xs text-n-slate-10">
              {{ item.typeLabel || t('FILE_PICKER.TYPE_FILE') }}
            </span>
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-slate-3 text-[0.6rem] font-bold text-n-slate-11 dark:bg-slate-800"
            >
              {{ itemExtension(item) }}
            </span>
          </button>
        </div>
      </div>

      <input
        ref="fileInput"
        type="file"
        class="hidden"
        :accept="accept || undefined"
        :multiple="multiple"
        @change="onFileChange"
      />
    </div>

    <template #footer>
      <div class="flex w-full min-w-0 items-center gap-3">
        <div
          class="hidden min-w-0 flex-1 items-center gap-4 overflow-x-auto text-[0.7rem] text-n-slate-10 sm:flex"
        >
          <span class="inline-flex shrink-0 items-center gap-1.5">
            <kbd class="rounded border border-n-slate-6 px-1 py-0.5 font-medium"
              >↑</kbd
            >
            <kbd class="rounded border border-n-slate-6 px-1 py-0.5 font-medium"
              >↓</kbd
            >
            {{ t('FILE_PICKER.HINT_NAVIGATE') }}
          </span>
          <span class="inline-flex shrink-0 items-center gap-1.5">
            <kbd class="rounded border border-n-slate-6 px-1 py-0.5 font-medium"
              >↵</kbd
            >
            {{ t('FILE_PICKER.HINT_SELECT') }}
          </span>
          <span
            v-if="sourceTabs.length > 1"
            class="inline-flex shrink-0 items-center gap-1.5"
          >
            <kbd class="rounded border border-n-slate-6 px-1 py-0.5 font-medium"
              >←</kbd
            >
            <kbd class="rounded border border-n-slate-6 px-1 py-0.5 font-medium"
              >→</kbd
            >
            {{ t('FILE_PICKER.HINT_SOURCE') }}
          </span>
        </div>
        <div class="min-w-0 shrink-0">
          <slot name="footer-start" />
        </div>
        <Button
          v-if="selectedCount > 0"
          color="blue"
          class="shrink-0"
          :label="resolvedConfirmLabel"
          :disabled="!canConfirm"
          :is-loading="uploading"
          type="button"
          @click="confirmSelection"
        />
      </div>
    </template>
  </Dialog>
</template>
